<template>
  <div style="display:none" />
</template>

<script>
import { mapGetters } from 'vuex'

// todo:: find bug that layer is grey until different prognose layer was chosen
export default {
  props: {
    loaded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('deployment', [
      'MapboxAccount',
      'DeploymentHasLayer',
      'DeploymentLayerName',
      'DeploymentCode',
    ]),
    ...mapGetters('config', {
      defaultBreakpoints: 'breakpoints',
      configLayers: 'layers',
    }),
    ...mapGetters('scenarios', [
      'year', 'forecast',
    ]),
    ...mapGetters('prognose', [
      'getHighlighted', 'labelLayer', 'visibility',
    ]),
    ...mapGetters('layers', {
      forecastLayers: 'getForecastLayerDetails',
    }),
    layer() {
      return this.forecastLayers.find(layer => layer.id === this.forecast)
    },
    isConfigurable() {
      return this.layer.config.isConfigurable
    },
    relevantForecast() {
      return !! this.layer
    },
    hasLayerData() {
      if (this.isConfigurable) {
        return !! this.configLayers[this.layer.id]?.source !== ''
      }

      return this.DeploymentHasLayer({ layer: this.layer?.id })
    },
    sourceName() {
      if (this.isConfigurable) {
        return this.configLayers[this.layer.id]?.source
      }

      return this.DeploymentLayerName({ layer: this.layer?.id })
    },
    url() {
      return `mapbox://${this.MapboxAccount}.${this.sourceName}`
    },
    breakpoints() {
      if (! this.layer.config.breakpoints) {
        return this.defaultBreakpoints
      }

      return this.layer.config.breakpoints
    },
    // whether to render continuous gradient based on layer config
    isGradient() {
      return !!this.layer?.config?.gradient
    },
  },
  watch: {
    loaded() {
      if (!this.layer) return
      if (! this.$store.map.getLayer(this.layer.id)) {
        this.addLayer()
      }
    },
    year() {
      this.updatePrognose()
    },
    getHighlighted() {
      this.updatePrognose()
    },
    forecast(next, previous) {
      if (this.$store.map.getLayer(previous)) {
        this.removeLayer({ id: previous })
      }

      if (! this.$store.map.getLayer(next)) {
        this.addLayer()
      } else {
        this.updatePrognose()
      }
    },
  },
  created() {
    if (this.loaded) {
      this.addLayer()
    }
  },
  beforeDestroy() {
    if (this.loaded) {
      this.removeLayer({ id: this.layer?.id })
    }
  },
  methods: {
    addLayer() {
      if (! this.layer) return
      if (! this.hasLayerData) return
      if (! this.relevantForecast) return

      const isDefaultHidden = !!this.layer.config?.defaultHidden

      if (isDefaultHidden) {
        this.$store.commit('prognose/setVisibility', { visibility: false })
      }

      this.$store.map.addSource(this.layer.id, {
        type: 'vector',
        url: this.url,
      })
      this.$store.map.addLayer({
        'id': this.layer.id,
        'type': 'fill',
        'source': this.layer.id,
        'source-layer': this.sourceName,
        'paint': {
          'fill-color': this.fillHexagons(),
          // Note: The legend opacity is linked
          'fill-opacity': isDefaultHidden && !this.visibility
            ? 0
            : this.fillOpacityExpression(),
          'fill-outline-color': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10,
            'hsla(232, 67%, 60%, 0)',
            22,
            'hsla(232, 67%, 60%, 0.9)',
          ],
        },
        'layout': {
          // Keep `defaultHidden` layers visible to mapbox so tiles preload;
          // visual hiding is handled via fill-opacity instead.
          'visibility': isDefaultHidden
            ? 'visible'
            : (this.visibility ? 'visible' : 'none'),
        },
      }, this.labelLayer)
    },

    fillOpacityExpression() {
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        10,
        0,
        10.2,
        0.7,
        13,
        0.4,
      ]
    },

    removeLayer({ id }) {
      if (this.$store.map.getLayer(id)) {
        this.$store.map.removeLayer(id)
        this.$store.map.removeSource(id)
      }
    },

    updatePrognose() {
      if (this.$store.map.getLayer(this.layer?.id)) {
        this.$store.map.setPaintProperty(this.layer?.id, 'fill-color', this.fillHexagons())
      }
    },

    fillHexagons() {
      // build property getter (respect availableYears)
      const propName = this.layer?.config?.availableYears
        ? `${this.layer.config.propertyWithData}${this.year}`
        : this.layer?.config?.propertyWithData

      const value = ['to-number', ['get', propName]]

      // highlight condition (any)
      const highlighted = Array.isArray(this.getHighlighted) ? this.getHighlighted : []
      const idProp = this.layer?.config?.propertyWithTileID
      let highlightCondition = null
      if (highlighted.length && idProp) {
        const equals = highlighted.map(tileID => ([
          '==',
          ['get', idProp],
          tileID,
        ]))
        highlightCondition = ['any', ...equals]
      }

      // colors
      const c0 = 'hsl(213, 50%, 80%)'
      const c1 = 'hsl(213, 65%, 55%)'
      const c2 = 'hsl(213, 80%, 35%)'
      const c3 = 'hsl(213, 95%, 20%)'
      const highlightColor = 'hsl(23, 100%, 42%)'
      const transparent = 'hsla(0, 0%, 0%, 0)'

      // Gradient branch
      if (this.isGradient) {
        // Prefer countryMinMax from layer config if available for current DeploymentCode
        const countryMinMax = this.layer?.config?.countryMinMax
        let min = Infinity
        let max = -Infinity
        if (countryMinMax && this.DeploymentCode && countryMinMax[this.DeploymentCode]) {
          const entry = countryMinMax[this.DeploymentCode]
          const eMin = Number(entry.min)
          const eMax = Number(entry.max)
          if (Number.isFinite(eMin) && Number.isFinite(eMax) && eMin < eMax) {
            min = eMin
            max = eMax
          }
        }

        // If invalid, fall back to breakpoints/defaults
        if (!(isFinite(min) && isFinite(max) && min < max)) {
          const bps = Array.isArray(this.breakpoints) ? [...this.breakpoints].map(Number).sort((a, b) => a - b) : []
          let baseBps = (bps.length >= 3) ? bps : [...this.defaultBreakpoints].map(Number).sort((a, b) => a - b)

          // ensure sensible defaults
          const valid = baseBps.length >= 3 && baseBps[0] < baseBps[1] && baseBps[1] < baseBps[2]
          if (!valid) {
            baseBps = [0.5, 1.5, 3]
          }

          min = 0
          max = baseBps[2]
        }

        // guard non-zero range
        if (min === max) max = min + 1

        const range = max - min
        const s1 = Number((min + range * 0.33).toFixed(6))
        const s2 = Number((min + range * 0.66).toFixed(6))

        const gradientExpr = [
          'interpolate',
          ['linear'],
          value,
          min, c0,
          s1, c1,
          s2, c2,
          max, c3,
        ]

        const expr = ['case']
        if (highlightCondition) expr.push(highlightCondition, highlightColor)
        expr.push(['<', value, 0.05], transparent)
        expr.push(gradientExpr)

        // emit range for legend consumers (optional)
        if (this.$emit) {
          this.$emit('gradient-range', { min, max })
        }

        return expr
      }

      // Stepped style (existing behavior)
      const styleCondition = ['case']

      highlighted.forEach(tileID => {
        if (!idProp) return
        styleCondition.push([
          '==',
          ['get', idProp],
          tileID,
        ])
        styleCondition.push(highlightColor)
      })

      return styleCondition.concat([
        ['<', value, 0.05], transparent,
        ['<=', value, this.breakpoints[0]], c0,
        ['<=', value, this.breakpoints[1]], c1,
        ['<=', value, this.breakpoints[2]], c2,
        ['>', value, this.breakpoints[2]], c3,
        transparent,
      ])
    },
  },
}
</script>